import { fiCurrencyToGQL, getCountryCodeFromCookie } from '../contexts/LocaleContext';
import {
  purchaseConsumerStoreCartMutation,
  purchaseSeries3UpgradeCartMutation,
  purchaseSubscriptionMutation,
} from '../graphql-operations';
import { purchaseMembershipUpgradeMutation } from '../graphql-operations/MembershipUpgrade.graphql';
import { purchaseMonthlyUpgradeMutation } from '../graphql-operations/MonthlyUpgrade.graphql';
import * as types from '../types';
import { gqlTypes } from '../types';
import { SERIES_3_UPGRADE_V2_COUPON_CODE } from '../views/Series3UpgradeV2/Series3UpgradePDPV2';
import { isBundleCartItem, isForModuleCartItem, isGiftCardCartItem } from './cart';
import client from './fi-api/client';
import Cookies from 'js-cookie';
import { COUPON_COOKIE_NAME, CouponCookieData } from './util';
import { getGwaEntitlementCookie, getGwpEntitlementCookie } from './setCouponCookie';
import { hardwareFirstGiftCartItems } from './hardwareFirstGiftCart';
import { GiftShipsTo } from '../types/gql-op-types';
import { isAmountGiftCardOnlyCart } from './hardwareFirstGiftPlans';
import store from './reduxStore';

const NO_PURCHASE_RESULTS_ERROR_MESSAGE = 'No data returned from purchase mutation';

// a gift shipped to the recipient puts their name and address on the order; the account name is the buyer's
function recipientShippingAddress({
  customerName,
  shippingAddress,
  giftRecipient,
}: types.CheckoutState): gqlTypes.GiftShippingAddressInput | undefined {
  if (giftRecipient?.shipsTo !== GiftShipsTo.RECIPIENT || !customerName || !shippingAddress) {
    return undefined;
  }
  return {
    firstName: customerName.firstName,
    lastName: customerName.lastName ?? '',
    street1: shippingAddress.line1,
    street2: shippingAddress.line2 ?? undefined,
    city: shippingAddress.city,
    state: shippingAddress.state,
    zipcode: shippingAddress.zip,
    phone: shippingAddress.phone ?? undefined,
  };
}

export async function executeStoreShopMutation(
  cart: types.StoreCart,
  checkoutState: types.CheckoutState,
  threeDSecureActionResultToken?: string,
  marketingEmailsOptedIn?: boolean,
  verizonPdpToken?: string,
  stripeConfirmationToken?: string,
): Promise<gqlTypes.CartPurchaseResult> {
  const giftCartItems = hardwareFirstGiftCartItems(cart, store.getState().config.products);
  const lineItems = giftCartItems ?? Object.values(cart.cartItems);
  const cartItems = lineItems.flatMap((cartItem): gqlTypes.StoreCartItemInput => {
    if (isForModuleCartItem(cartItem)) {
      throw new Error('Unxpected cart item type');
    }

    // Be explicit about mapping to gqlTypes here to avoid any issues with the GraphQL schema
    // return cartItem; works but doesn't report type errors if cartItem has more properties than expected
    if (isBundleCartItem(cartItem)) {
      return {
        cartItemId: cartItem.cartItemId,
        lineItem: cartItem.lineItem,
        quantity: cartItem.quantity,
        subscriptionLineItem: cartItem.subscriptionLineItem,
        addonLineItems: cartItem.addonLineItems,
        isGift: cartItem.isGift,
      };
    }

    if (isGiftCardCartItem(cartItem)) {
      return {
        cartItemId: cartItem.cartItemId,
        lineItem: cartItem.lineItem,
        quantity: cartItem.quantity,
        giftCardDelivery: cartItem.giftCardDelivery,
      };
    }

    return {
      cartItemId: cartItem.cartItemId,
      lineItem: cartItem.lineItem,
      quantity: cartItem.quantity,
      ultraAttachment: cartItem.ultraAttachment,
    };
  });

  // GWA attribution: the coupon cookie carries `source` + the rescue's program id when the coupon was
  // applied via the nano offer exchange. Only forward them when the cookie matches the coupon being purchased.
  const couponCookie = Cookies.getJSON(COUPON_COOKIE_NAME) as CouponCookieData | undefined;
  const gwaSource = couponCookie?.code === cart.couponCode ? couponCookie?.source : undefined;
  const gwaProgramId = couponCookie?.code === cart.couponCode ? couponCookie?.rescueGwaProgramId : undefined;
  // The signed entitlement lives in its own cookie (the slug flow has no code to match on); the server
  // requires it for a GWA alias to resolve, and binds the code to the entitlement's program itself.
  const gwaToken = getGwaEntitlementCookie();
  // Same forwarding for the GwP partner single-code entitlement, stored by the partner slug landing.
  const gwpToken = getGwpEntitlementCookie();

  const { data } = await client.mutate<
    gqlTypes.ECOMMERCE_purchaseConsumerStoreCart,
    gqlTypes.ECOMMERCE_purchaseConsumerStoreCartVariables
  >({
    mutation: purchaseConsumerStoreCartMutation,
    variables: {
      input: {
        cartItems,
        ...(giftCartItems && { isGift: true, giftRecipient: checkoutState.giftRecipient }),
        couponCode: cart.couponCode,
        gwaSource,
        gwaProgramId,
        gwaToken,
        gwpToken,
        redeemedGiftCardCode: cart.redeemedGiftCardCode,
        referralCode: cart.referralCode,
        referralSource: cart.referralSource,
        // the hidden shipping list still picks a code on a card-only checkout; nothing ships
        shippingCode: isAmountGiftCardOnlyCart(cart) ? undefined : checkoutState.shippingCode,
        giftShippingAddress: cart.giftShippingAddress ?? (giftCartItems && recipientShippingAddress(checkoutState)),
        threeDSecureActionResultToken: threeDSecureActionResultToken,
        stripeConfirmationToken,
        currency: fiCurrencyToGQL(cart.currency),
        countryCode: getCountryCodeFromCookie(),
        marketingEmailsOptedIn,
        verizonPdpToken,
      },
    },
  });
  if (!data) {
    throw new Error(NO_PURCHASE_RESULTS_ERROR_MESSAGE);
  }

  return data.purchaseConsumerStoreCart;
}

export async function executeUpgradeShopMutation(
  cart: types.Cart,
  checkoutState: types.CheckoutState,
  threeDSecureActionResultToken?: string,
  marketingEmailsOptedIn?: boolean,
): Promise<gqlTypes.CartPurchaseResult> {
  const cartItems = Object.values(cart.cartItems).flatMap((cartItem): gqlTypes.ForModuleBundleCartItemInput => {
    if (isForModuleCartItem(cartItem) && isBundleCartItem(cartItem)) {
      // Be explicit about mapping to gqlTypes here to avoid any issues with the GraphQL schema
      // return cartItem; works but doesn't report type errors if cartItem has more properties than expected
      return {
        cartItemId: cartItem.cartItemId,
        forModuleId: cartItem.forModuleId,
        lineItem: cartItem.lineItem,
        quantity: cartItem.quantity,
        subscriptionLineItem: cartItem.subscriptionLineItem,
      };
    }

    throw new Error('Unxpected cart item type');
  });

  const { data } = await client.mutate<
    gqlTypes.ECOMMERCE_purchaseSeries3UpgradeCart,
    gqlTypes.ECOMMERCE_purchaseSeries3UpgradeCartVariables
  >({
    mutation: purchaseSeries3UpgradeCartMutation,
    variables: {
      input: {
        cartItems,
        shippingCode: checkoutState.shippingCode,
        couponCode: cart.couponCode,
        isUpgradeV2: cart.couponCode === SERIES_3_UPGRADE_V2_COUPON_CODE,
        threeDSecureActionResultToken: threeDSecureActionResultToken,
        currency: fiCurrencyToGQL(cart.currency),
        countryCode: getCountryCodeFromCookie(),
        marketingEmailsOptedIn,
      },
    },
  });
  if (!data) {
    throw new Error(NO_PURCHASE_RESULTS_ERROR_MESSAGE);
  }

  return data.purchaseSeries3UpgradeCart;
}

export async function executeSubscriptionShopMutation(
  cart: types.Cart,
  threeDSecureActionResultToken?: string,
  marketingEmailsOptedIn?: boolean,
): Promise<gqlTypes.CartPurchaseResult> {
  const cartItem = Object.values(cart.cartItems)[0];
  if (!cartItem || !isForModuleCartItem(cartItem)) {
    throw new Error('Unxpected cart item type');
  }

  const { data } = await client.mutate<
    gqlTypes.ECOMMERCE_purchaseSubscription,
    gqlTypes.ECOMMERCE_purchaseSubscriptionVariables
  >({
    mutation: purchaseSubscriptionMutation,
    variables: {
      input: {
        sku: cartItem.lineItem.sku,
        moduleId: cartItem.forModuleId,
        redeemGiftCard: cart.redeemedGiftCardCode ? { redemptionCode: cart.redeemedGiftCardCode } : undefined,
        couponCode: cart.couponCode,
        threeDSecureActionResultToken: threeDSecureActionResultToken,
        currency: fiCurrencyToGQL(cart.currency),
        countryCode: getCountryCodeFromCookie(),
        marketingEmailsOptedIn,
      },
    },
  });
  if (!data) {
    throw new Error(NO_PURCHASE_RESULTS_ERROR_MESSAGE);
  }

  return data.purchaseSubscription;
}

export async function executeMembershipUpgradeMutation(
  cart: types.Cart,
  threeDSecureActionResultToken?: string,
  marketingEmailsOptedIn?: boolean,
): Promise<gqlTypes.CartPurchaseResult> {
  // There should be one and only one cart item in the membership upgrade cart
  const cartItem = Object.values(cart.cartItems)[0];
  if (!cartItem || !isForModuleCartItem(cartItem)) {
    throw new Error('Unxpected cart item type');
  }

  const { data } = await client.mutate<
    gqlTypes.ECOMMERCE_purchaseMembershipUpgrade,
    gqlTypes.ECOMMERCE_purchaseMembershipUpgradeVariables
  >({
    mutation: purchaseMembershipUpgradeMutation,
    variables: {
      input: {
        sku: cartItem.lineItem.sku,
        moduleId: cartItem.forModuleId,
        threeDSecureActionResultToken: threeDSecureActionResultToken,
        currency: fiCurrencyToGQL(cart.currency),
        countryCode: getCountryCodeFromCookie(),
        marketingEmailsOptedIn,
      },
    },
  });
  if (!data) {
    throw new Error(NO_PURCHASE_RESULTS_ERROR_MESSAGE);
  }

  return data.purchaseMembershipUpgrade;
}

export async function executeMonthlyUpgradeMutation(
  cart: types.Cart,
  threeDSecureActionResultToken?: string,
  marketingEmailsOptedIn?: boolean,
): Promise<gqlTypes.CartPurchaseResult> {
  // There should be one and only one cart item in the monthly upgrade cart
  const cartItem = Object.values(cart.cartItems)[0];
  if (!cartItem || !isForModuleCartItem(cartItem)) {
    throw new Error('Unxpected cart item type');
  }

  const { data } = await client.mutate<
    gqlTypes.ECOMMERCE_purchaseMonthlyUpgrade,
    gqlTypes.ECOMMERCE_purchaseMonthlyUpgradeVariables
  >({
    mutation: purchaseMonthlyUpgradeMutation,
    variables: {
      input: {
        sku: cartItem.lineItem.sku,
        moduleId: cartItem.forModuleId,
        threeDSecureActionResultToken: threeDSecureActionResultToken,
        currency: fiCurrencyToGQL(cart.currency),
        countryCode: getCountryCodeFromCookie(),
        marketingEmailsOptedIn,
      },
    },
  });
  if (!data) {
    throw new Error(NO_PURCHASE_RESULTS_ERROR_MESSAGE);
  }

  return data.purchaseMonthlyUpgradePromotion;
}
